/**
 * Idle proof for the recovery **IDR request**, and for nothing else.
 *
 * This gate exists because a keyframe hitches the picture, and a hitch mid-firefight is
 * worse than the stale quality it resolves. It once also gated the bitrate climb, which
 * was a mistake: a `setParameters` bitrate change does not force a keyframe and does not
 * hitch, so gating it here demanded five idle seconds that a person playing a game never
 * gives, and a single congestion cut pinned the stream low for the whole session. The
 * climb now runs during play; only the IDR consults this gate.
 *
 * `isPlayerInteracting` is fail-closed on "the host has never received a playerActivity
 * message", which is unreachable for a guest that re-establishes its connection and then
 * sits still: it never sends activity, so the gate could never open. Per-peer activity
 * state is dropped on reconnect, so every reconnect re-enters that state.
 *
 * Silence is only evidence of idleness if we know activity *would* have been reported.
 * The guest attaches its input listeners when the data channel opens and emits within
 * PLAYER_ACTIVITY_MIN_INTERVAL_MS of any input, so silence measured from the moment the
 * host started watching is positive proof of idle rather than an absence of evidence.
 */

import { RECOVERY_IDLE_BEFORE_CLIMB_MS, isPlayerInteracting } from './webrtcLatencyContracts';

/**
 * PRODUCT DECISION — currently ON.
 *
 * When true, a guest that has been silent since the host started watching for activity is
 * treated as idle once the silence covers the full idle window. Turning this off restores
 * the strict "no climb until activity has been seen at least once" behaviour, which also
 * restores the reconnect-then-sit-still stall.
 */
export const TREAT_SILENCE_SINCE_BASELINE_AS_IDLE = true;

/**
 * Interaction gate for the climb, with a baseline that makes silence meaningful.
 *
 * `activityBaselineAtMs` is when the host started watching this peer for activity (null
 * when it never did — then this stays fail-closed exactly like `isPlayerInteracting`).
 */
export function isPlayerInteractingSinceBaseline(
  lastActivityAtMs: number | null,
  activityBaselineAtMs: number | null,
  nowMs: number,
  hasEverReceivedActivity: boolean,
  idleBeforeClimbMs: number = RECOVERY_IDLE_BEFORE_CLIMB_MS,
): boolean {
  if (hasEverReceivedActivity && lastActivityAtMs !== null) {
    return isPlayerInteracting(lastActivityAtMs, nowMs, true, idleBeforeClimbMs);
  }

  if (!TREAT_SILENCE_SINCE_BASELINE_AS_IDLE || activityBaselineAtMs === null) {
    return isPlayerInteracting(
      lastActivityAtMs,
      nowMs,
      hasEverReceivedActivity,
      idleBeforeClimbMs,
    );
  }

  return nowMs - activityBaselineAtMs < idleBeforeClimbMs;
}
